import { useEffect, useState } from 'react';

interface LinkPreviewProps {
  url: string;
}

/** A link that IS the media, rather than a page describing it. */
const IMAGE_FILE = /\.(jpe?g|png|gif|webp|avif|bmp|svg)(\?|#|$)/i;
const VIDEO_FILE = /\.(mp4|webm|ogv|mov|m4v)(\?|#|$)/i;

type Embed =
  | { kind: 'iframe'; src: string; ratio?: string; height?: number; title: string }
  | { kind: 'image'; src: string }
  | { kind: 'video'; src: string };

/**
 * What this URL can be shown as, decided from the URL alone — no network, so it
 * renders instantly and keeps working when the metadata API is rate limited or
 * the site blocks scrapers (Instagram and TikTok both do; asking microlink for
 * an Instagram post returns a login-wall placeholder, not the photo).
 */
function resolveEmbed(url: string): Embed | null {
  const yt = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|shorts\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
  if (yt?.[1]) {
    return { kind: 'iframe', src: `https://www.youtube.com/embed/${yt[1]}`, ratio: '16 / 9', title: 'YouTube video' };
  }

  const spotify = url.match(/open\.spotify\.com\/(track|album|playlist|episode|show)\/([^?#/]+)/);
  if (spotify?.[1] && spotify[2]) {
    return { kind: 'iframe', src: `https://open.spotify.com/embed/${spotify[1]}/${spotify[2]}`, height: 152, title: 'Spotify player' };
  }

  // /@handle/video/123 and the short v/photo forms both carry the numeric id.
  const tiktok = url.match(/tiktok\.com\/(?:.*\/video\/|v\/)(\d+)/);
  if (tiktok?.[1]) {
    return { kind: 'iframe', src: `https://www.tiktok.com/embed/v2/${tiktok[1]}`, height: 560, title: 'TikTok video' };
  }

  const instagram = url.match(/instagram\.com\/(p|reel|reels|tv)\/([A-Za-z0-9_-]+)/);
  if (instagram?.[2]) {
    // `reels` is the app's own plural; the embed endpoint only knows `reel`.
    const kind = instagram[1] === 'reels' ? 'reel' : instagram[1];
    return { kind: 'iframe', src: `https://www.instagram.com/${kind}/${instagram[2]}/embed`, height: 540, title: 'Instagram post' };
  }

  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo?.[1]) {
    return { kind: 'iframe', src: `https://player.vimeo.com/video/${vimeo[1]}`, ratio: '16 / 9', title: 'Vimeo video' };
  }

  if (VIDEO_FILE.test(url)) return { kind: 'video', src: url };
  if (IMAGE_FILE.test(url)) return { kind: 'image', src: url };

  return null;
}

/**
 * One entry per URL for the life of the session. A link shared by several people
 * — or the same yap re-rendered as the feed scrolls — then costs one request
 * instead of one per card, which matters because the metadata API is rate
 * limited per IP. `null` is cached too: a link that has no preview should not be
 * retried on every render.
 */
const metaCache = new Map<string, any>();

const cardStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 8,
  borderRadius: 12,
  overflow: 'hidden',
  textDecoration: 'none',
};

/**
 * Renders a link posted in a yap as the thing it points at: the video plays, the
 * image shows, and anything else falls back to a title/description card.
 *
 * Note on sizing: every box here sets its height inline. `aspect-video` has no
 * rule in the committed pre-compiled stylesheet, so the previous version's image
 * wrapper — an `aspect-video` div whose only child was `absolute inset-0` —
 * computed to zero height, and no preview image has ever been visible anywhere
 * in the app.
 */
export function LinkPreview({ url }: LinkPreviewProps) {
  const embed = resolveEmbed(url);
  // Keyed on the resolved target so the effect doesn't re-run on every render
  // just because resolveEmbed returns a fresh object each time.
  const embedKey = embed ? `${embed.kind}:${embed.src}` : '';

  const [data, setData] = useState<any>(() => metaCache.get(url) ?? null);
  const [loading, setLoading] = useState(() => !embed && !metaCache.has(url));

  // Hooks run unconditionally, ABOVE every early return. They used to sit below
  // the YouTube and Spotify branches, so a component instance reused for a
  // different url (which the feed does) could go from three hooks to one and
  // trip React's "rendered fewer hooks than expected".
  useEffect(() => {
    if (embedKey) return;                       // nothing to look up
    if (metaCache.has(url)) {
      setData(metaCache.get(url));
      setLoading(false);
      return;
    }

    let alive = true;
    setLoading(true);
    fetch(`https://api.microlink.io/?url=${encodeURIComponent(url)}`)
      .then(res => res.json())
      .then(result => {
        const payload = result?.status === 'success' ? result.data : null;
        metaCache.set(url, payload);
        if (alive) { setData(payload); setLoading(false); }
      })
      .catch(e => {
        console.error('[LinkPreview] Fetch error:', e);
        metaCache.set(url, null);
        if (alive) setLoading(false);
      });

    return () => { alive = false; };
  }, [url, embedKey]);

  // Nothing below this point may call a hook.

  if (embed?.kind === 'iframe') {
    return (
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          ...cardStyle,
          background: '#000',
          border: '1px solid rgba(0,0,0,0.08)',
          ...(embed.ratio ? { aspectRatio: embed.ratio } : { height: embed.height }),
        }}
      >
        {/* Hosts here must also be in index.html's CSP frame-src, or the
            browser refuses the frame and it renders as an empty box.
            YouTube's player rejects embeds that send no referrer. */}
        <iframe
          src={embed.src}
          title={embed.title}
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
        />
      </div>
    );
  }

  if (embed?.kind === 'video') {
    return (
      <div onClick={(e) => e.stopPropagation()} style={{ ...cardStyle, background: '#000' }}>
        {/* playsInline keeps iOS from hijacking the whole screen the moment it
            starts, which is what a bare <video> does inside a WebView. */}
        <video
          src={embed.src}
          controls
          playsInline
          preload="metadata"
          style={{ width: '100%', maxHeight: 420, display: 'block', objectFit: 'contain' }}
        />
      </div>
    );
  }

  if (embed?.kind === 'image') {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        style={{ ...cardStyle, border: '1px solid rgba(0,0,0,0.08)' }}
      >
        <img
          src={embed.src}
          alt=""
          loading="lazy"
          style={{ width: '100%', maxHeight: 420, objectFit: 'cover', display: 'block' }}
        />
      </a>
    );
  }

  if (loading) {
    return (
      <div
        className="bg-black/5 dark:bg-white/5"
        style={{ ...cardStyle, height: 96, border: '1px solid rgba(0,0,0,0.06)' }}
      />
    );
  }

  if (!data) return null;

  // Some pages advertise the video itself; play it rather than showing a poster
  // frame the reader has to click through.
  const videoUrl: string | undefined = data.video?.url;
  // A data: URI here is the scraper hitting a login wall (Instagram serves a
  // placeholder PNG that way), and inlining kilobytes of base64 to paint a grey
  // rectangle helps nobody.
  const imageUrl: string | undefined =
    typeof data.image?.url === 'string' && !data.image.url.startsWith('data:') ? data.image.url : undefined;

  if (!data.title && !imageUrl && !videoUrl) return null;

  const host = (() => {
    try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
  })();

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="bg-black/5 dark:bg-[#121212]"
      style={{ ...cardStyle, border: '1px solid rgba(0,0,0,0.10)' }}
    >
      {videoUrl ? (
        <video
          src={videoUrl}
          controls
          playsInline
          preload="metadata"
          onClick={(e) => e.preventDefault()}
          style={{ width: '100%', maxHeight: 420, background: '#000', display: 'block' }}
        />
      ) : imageUrl ? (
        <img
          src={imageUrl}
          alt={data.title || 'Link preview'}
          loading="lazy"
          style={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', display: 'block' }}
        />
      ) : null}

      <div style={{ padding: 12 }}>
        {data.title && (
          <h4
            className="text-[#333333] dark:text-[#E5E5E5] truncate"
            style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}
          >
            {data.title}
          </h4>
        )}
        {data.description && (
          <p
            className="text-[#666666] dark:text-[#a0a0a0]"
            style={{
              fontSize: 12,
              marginBottom: 8,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {data.description}
          </p>
        )}
        <div className="flex items-center gap-1.5">
          {data.logo?.url && !String(data.logo.url).startsWith('data:') && (
            <img src={data.logo.url} alt="" style={{ width: 14, height: 14, borderRadius: 2, objectFit: 'cover' }} />
          )}
          <span
            className="text-[#999999] dark:text-[#666666]"
            style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}
          >
            {data.publisher || host}
          </span>
        </div>
      </div>
    </a>
  );
}
